import * as React from 'react'
import { StyleSheet, View, ViewStyle } from 'react-native'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { Appbar } from 'react-native-paper'
import { NavigationRoute } from '@/main/config/router-navigation'
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons'
import { MessagePayload, onMessage } from 'firebase/messaging'
import { messaging } from '@/main/config/firebaseConfig'
import { useAuth, useErrorHandler, useMediaQueries, useNotify, useTheme, useThemeClient } from '@/presentation/hooks'
import { Text, LogoTheme, NavigationPagesProps, isOpenUserMenuState, IconButton, Badge, notificationsState, isOpenNotificationsModalState, localStorageState, HStack } from '@/presentation/components'
import { ReceivedNotificationModel } from '@/domain/models'
import { GetClientNotifications, SaveNotificationToken } from '@/domain/usecases'

export const APP_BAR_HEIGTH = 64
export const PADDING_STATUS_BAR_IOS = 24

type HeaderProps = {
  saveNotificationToken: SaveNotificationToken
  getNotifications: GetClientNotifications
} & NavigationPagesProps

export const Header: React.FC<HeaderProps> = (props) => {
  const theme = useTheme()
  const { isAdmin, getCurrentClient } = useAuth()
  const { openInPWA, openInIOS, isMobile } = useMediaQueries()
  const { getThemeClient } = useThemeClient()
  const [notifications, setNotifications] = useRecoilState(notificationsState)
  const notify = useNotify()
  const setOpenUserMenu = useSetRecoilState(isOpenUserMenuState)
  const setOpenNotificationsMenu = useSetRecoilState(isOpenNotificationsModalState)
  const handleError = useErrorHandler()
  const localStorageAdapter = useRecoilValue(localStorageState)
  const currentClient = getCurrentClient()

  const themeClient = getThemeClient()
  const userAdministrator = isAdmin()

  const contentTitleStyle: ViewStyle = {
    alignItems: 'flex-start'
  }

  if (!isMobile || userAdministrator) {
    contentTitleStyle.alignItems = 'center'
  }

  React.useEffect(() => {
    if (!userAdministrator) {
      window.addEventListener('focus', handleSearchNotifications)
      handleSaveNotificationToken()

      return () => {
        window.removeEventListener('focus', handleSearchNotifications)
      }
    }
  }, [])

  const handleSaveNotificationToken = (): void => {
    const tokenCliente = localStorageAdapter.get(SaveNotificationToken.RN.LOCAL_STORAGE_TOKEN)

    if (tokenCliente) {
      props.saveNotificationToken
        .save({
          codigoCliente: +currentClient,
          tokenDispositivo: tokenCliente
        })
        .then(() => { handleSearchNotifications() })
        .catch(handleError)
    } else {
      handleSearchNotifications()
    }
  }

  const handleSearchNotifications = React.useCallback((): void => {
    +currentClient > 0 && props.getNotifications
      .get({ codigo: +currentClient })
      .then(setNotifications)
      .catch(handleError)
  }, [currentClient])

  const onMessageListener = async (): Promise<void> => {
    await new Promise(() => {
      messaging().then((messagingInstance) => {
        if (messagingInstance) {
          onMessage(messagingInstance, (payload: MessagePayload) => {
            const notification = payload.data as ReceivedNotificationModel

            if (notification) {
              handleSearchNotifications()

              notify.info(notification.body)
            }
          })
        }
      }).catch(error => {
        console.error('Error getting messaging instance:', error)
      })
    })
  }

  if (!userAdministrator && +currentClient > 0) {
    void onMessageListener()
  }

  const ContentTitle: React.FC = () => (
    <Appbar.Content
      title={
        <View style={styles.logoArea}>
          <LogoTheme height={40} />
          <Text
            onPress={() => props.navigation.navigate(NavigationRoute.Home)}
            style={{
              ...styles.title,
              ...(isMobile && { fontSize: 16 })
            }}
            variant='h6'
          >
            Portal do Cliente
          </Text>
        </View>
      }
      style={ contentTitleStyle }
    />
  )

  return (
    <Appbar.Header style={{
      ...styles.appBar,
      backgroundColor: themeClient.mainColor,
      ...(openInPWA && openInIOS && { paddingTop: PADDING_STATUS_BAR_IOS })
    }}>
      <Appbar.Action
        icon={(props: any) => <MaterialCommunityIcons name='menu' {...props}/>}
        onPress={() => props.navigation.toggleDrawer()}
        isLeading
        testID="menu-geral"
        color={themeClient.menuColor}
      />
      <ContentTitle />
      <HStack>
        {!isAdmin() && <>
          <IconButton
            color={themeClient.menuColor}
            onPress={() => { setOpenNotificationsMenu(true) }}
            icon={props => <MaterialIcons name='notifications' {...props} />}
          />
          {notifications && <Badge
            label={notifications?.length}
            labelStyle={{ fontSize: 8, color: theme.colors.white }}
            style={{ marginLeft: -24, marginTop: 16, paddingRight: 1, paddingLeft: 1, minWidth: 16, height: 16 }}
            color={theme.colors.error}
          />}
        </>
        }
        <IconButton
          style={{ marginLeft: 8 }}
          color={themeClient.menuColor}
          onPress={() => {
            setOpenUserMenu(true)
          }}
          testID="menu-usuario"
          icon={props => <MaterialIcons name='account-circle' {...props} />}
        />
      </HStack>
    </Appbar.Header>
  )
}

const styles = StyleSheet.create({
  appBar: {
    minHeight: APP_BAR_HEIGTH
  },
  logoArea: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row'
  },
  title: {
    color: '#FFF',
    flex: 1
  }
})
